import {checked_option_scroller} from '@bitstillery/common/lib/scroll'
import {classes, copy_object} from '@bitstillery/common/lib/utils'
import {CheckboxGroup, Icon} from '@bitstillery/common/components'
import {MithrilComponent} from '@bitstillery/mithril'
import {$t} from '@bitstillery/common/app'
import m from '@bitstillery/mithril'

import type {FilterSelectMultipleState, FilterI18nResolved} from '@bitstillery/common/types'
import type {CheckboxOption} from '@bitstillery/common/components/groups/checkbox/checkbox'

interface FilterSelectMultipleAttrs {
    className: string
    filter_count?: boolean
    filter: FilterSelectMultipleState
    total?: number | null
    translate?: FilterI18nResolved | undefined
}

export class FilterSelectMultiple extends MithrilComponent<FilterSelectMultipleAttrs> {
    scroll_selection_into_view = checked_option_scroller()

    count_selected_options(vnode: m.Vnode<FilterSelectMultipleAttrs>) {
        const exclude_ids: (string | number)[] = []
        const selection = vnode.attrs.filter.selection as (string | number)[]
        for (const selected_option of selection) {
            const parent = vnode.attrs.filter.options.find((i) => i[0] === selected_option)
            if (parent && parent.length >= 4 && Array.isArray(parent[3])) {
                exclude_ids.push.apply(
                    exclude_ids,
                    parent[3].filter((i) => selection.includes(i[0])).map((i) => i[0]),
                )
            }
        }

        return selection.length - exclude_ids.length
    }

    deactivate_filter(vnode: m.Vnode<FilterSelectMultipleAttrs>) {
        vnode.attrs.filter.selection.splice(0)
    }

    /**
     * Filter checkbox (sub)options that have a count or selection.
     * @returns Array
     */
    filtered_options(vnode: m.Vnode<FilterSelectMultipleAttrs>) {
        // Make a copy, or filtering the children in the next map will Mithril redraw infinitely.
        const options = copy_object<CheckboxOption[]>(vnode.attrs.filter.options)
        const selection = vnode.attrs.filter.selection as (string | number)[]
        return options
            .map((option) => {
                if (vnode.attrs.filter_count && option[3]) {
                    option[3] = option[3].filter((child_option) => {
                        if (child_option.length >= 3 && child_option[2] === 0) {
                            return selection.includes(child_option[0])
                        }
                        return true
                    })
                }
                return option
            })
            .filter((option) => {
                // The whole parent item has no count; remove it unless there
                // Is still a selected subitem child.
                if (vnode.attrs.filter_count && option[2] === 0) {
                    if (option[3]) {
                        return option[3].some((child_option) => selection.includes(child_option[0]))
                    }
                    return selection.includes(option[0])
                }

                return true
            })
    }

    /**
     * A filter is inert when it cannot narrow the collection down any further;
     * i.e. when it has no options left, or when its single remaining option
     * already covers every item in the collection. An active filter is never
     * inert; its own selection is what makes the option cover everything, and
     * the user must be able to undo it.
     */
    is_inert(vnode: m.Vnode<FilterSelectMultipleAttrs>, filtered_options: CheckboxOption[]) {
        if (filtered_options.length === 0) {
            return true
        }
        if (vnode.attrs.filter.selection.length) {
            return false
        }
        if (filtered_options.length > 1) {
            return false
        }
        // The one option left, as checked above.
        const count = filtered_options[0]?.[2]
        const total = vnode.attrs.total
        // Without counts to compare, fall back to treating a lone option as inert.
        if (typeof count !== 'number' || typeof total !== 'number') {
            return true
        }
        return count === total
    }

    view(vnode: m.Vnode<FilterSelectMultipleAttrs>) {
        const filtered_options = this.filtered_options(vnode)
        const inert = this.is_inert(vnode, filtered_options)
        return (
            <div
                className={classes('c-filter-select-multiple', 'filter', vnode.attrs.className, {
                    active: vnode.attrs.filter.selection.length,
                    collapsed: vnode.attrs.filter.collapsed || vnode.attrs.filter.disabled,
                    disabled: inert || vnode.attrs.filter.disabled,
                })}
            >
                <div
                    className='title'
                    onclick={() => {
                        if (!inert) {
                            vnode.attrs.filter.collapsed = !vnode.attrs.filter.collapsed
                        }
                    }}
                >
                    <div class='name'>
                        <div class='filter-icon'>
                            <Icon
                                active={vnode.attrs.filter.selection.length}
                                className='btn-filter-toggle'
                                name={(() => {
                                    if (vnode.attrs.filter.icon) {
                                        return vnode.attrs.filter.icon
                                    }
                                    return vnode.attrs.filter.selection.length ? 'filterRemove' : 'filterPlus'
                                })()}
                                onclick={(e) => {
                                    e.stopPropagation()
                                    this.deactivate_filter(vnode)
                                }}
                                tip={() =>
                                    vnode.attrs.filter.selection.length > 0
                                        ? $t('filters.actions.deactivate_filter')
                                        : $t('filters.inactive_filter')
                                }
                            />
                        </div>
                        {$t(vnode.attrs.filter.title)}
                        {inert && filtered_options.length === 1 && <span>&nbsp;({filtered_options[0]?.[1]})</span>}
                        {vnode.attrs.filter_count && vnode.attrs.filter.selection.length > 0 && (
                            <div class='count'>
                                (
                                {$t('filters.selected', {
                                    count: this.count_selected_options(vnode),
                                })}
                                )
                            </div>
                        )}
                    </div>
                    <Icon className='btn-collapse' name={vnode.attrs.filter.collapsed ? 'chevronUp' : 'chevronDown'} />
                </div>
                <div
                    class='items'
                    oncreate={(items: m.VnodeDOM) => this.scroll_selection_into_view(items.dom)}
                    onupdate={(items: m.VnodeDOM) => this.scroll_selection_into_view(items.dom)}
                >
                    {vnode.attrs.filter.help && <p className={'help-text'}>{vnode.attrs.filter.help}</p>}
                    {!inert && vnode.attrs.filter.$selection && (
                        <CheckboxGroup
                            disabled={vnode.attrs.filter.disabled ?? false}
                            filter_count={vnode.attrs.filter_count}
                            model={vnode.attrs.filter.$selection}
                            options={filtered_options}
                            translate={vnode.attrs.translate}
                        />
                    )}
                </div>
            </div>
        )
    }
}
